import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { CalendarIcon, Clock, Loader2, AlertCircle } from "lucide-react";
import { format, isSameDay } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ScheduleAppointment() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const actorId = searchParams.get("actor_id");

  const [sessionValid, setSessionValid] = useState(null); // null=loading, true=valid, false=invalid
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const minDate = new Date();
  minDate.setHours(0, 0, 0, 0);

  // Set page title
  useEffect(() => {
    document.title = "Campos | Booking Appointment";
    return () => {
      document.title = "Campos | AI Agents"; // Reset on unmount
    };
  }, []);

  useEffect(() => {
    if (!sessionId || !actorId) {
      setSessionValid(false);
      setLoadingServices(false);
      return;
    }
    const validateSession = async () => {
      try {
        const res = await axios.get(`${API}/public/chat/validate-session`, {
          params: { session_id: sessionId, actor_id: actorId },
        });
        setSessionValid(res.data?.valid === true);
        if (res.data?.valid !== true) setLoadingServices(false);
      } catch (err) {
        console.warn("Session validation failed:", err);
        setSessionValid(false);
        setLoadingServices(false);
      }
    };
    validateSession();
  }, [sessionId, actorId]);

  useEffect(() => {
    if (sessionValid !== true) return;
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/public/services`);
        setServices(res.data?.services || []);
      } catch (err) {
        console.error("Failed to fetch services:", err);
        toast.error("Failed to load services");
      } finally {
        setLoadingServices(false);
      }
    };
    fetchServices();
  }, [sessionValid]);

  useEffect(() => {
    if (sessionValid !== true || !sessionId || !actorId) return;
    const fetchSessionInfo = async () => {
      try {
        const res = await axios.get(`${API}/public/chat/session-info`, {
          params: { session_id: sessionId, actor_id: actorId },
        });
        if (res.data?.name) setName(res.data.name);
        if (res.data?.phone) setPhone(res.data.phone);
      } catch (err) {
        console.warn("Could not fetch session info:", err);
      }
    };
    fetchSessionInfo();
  }, [sessionValid, sessionId, actorId]);

  useEffect(() => {
    if (!serviceId || !date) {
      setAvailableSlots([]);
      return;
    }
    const fetchSlots = async () => {
      setLoadingSlots(true);
      setAvailableSlots([]);
      setTime("");
      try {
        const res = await axios.get(`${API}/appointments-public/available-slots`, {
          params: {
            service_id: serviceId,
            date: format(date, "yyyy-MM-dd"),
          },
        });
        const slots = res.data?.data?.slots || [];
        // Filter only slots with available === true
        const available = slots.filter((s) => s.available === true);
        setAvailableSlots(available);
        // If no available slots, show message
        if (available.length === 0 && slots.length > 0) {
          toast.info("No available slots for this date. Please select another date.");
        }
      } catch (err) {
        console.error("Failed to fetch available slots:", err);
        toast.error("Failed to load available slots");
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [serviceId, date]);

  const getServiceLabel = (s) => s.name ?? s.title ?? s.service_name ?? "Service";
  const getServiceValue = (s) => s.id ?? s._id ?? s.service_id ?? getServiceLabel(s);

  // Check if slot is in the past (for today only) - uses user's local timezone
  const isSlotInPast = (selectedDate, slotTime) => {
    if (!selectedDate || !slotTime) return false;
    const now = new Date();
    if (!isSameDay(selectedDate, now)) return false;
    const [h, m] = slotTime.split(":").map(Number);
    const slotMinutes = (h || 0) * 60 + (m || 0);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return slotMinutes < nowMinutes;
  };

  // Step-by-step: each field enabled only when previous step is filled
  const dateEnabled = !!serviceId;
  const timeEnabled = dateEnabled && !!date && !loadingSlots;
  const detailsEnabled = timeEnabled && !!time;

  const handleServiceChange = (val) => {
    const newVal = val === "__clear__" ? "" : val;
    setServiceId(newVal);
    if (!newVal) {
      setDate(null);
      setTime("");
      setName("");
      setPhone("");
      setNotes("");
    }
  };

  const handleDateChange = (val) => {
    setDate(val ?? null);
    setAvailableSlots([]);
    if (!val) {
      setTime("");
      setName("");
      setPhone("");
      setNotes("");
    }
  };

  const handleTimeChange = (val) => {
    setTime(val);
    if (!val) setNotes("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!serviceId) {
      toast.error("Please select a service first");
      return;
    }
    if (!date) {
      toast.error("Please select a date first");
      return;
    }
    if (!time) {
      toast.error("Please select a time first");
      return;
    }

    const selected = services.find((s) => getServiceValue(s) === serviceId);
    const serviceName = selected ? getServiceLabel(selected) : serviceId;
    const selectedSlot = availableSlots.find((s) => s.time === time);
    const duration = selectedSlot?.duration ?? 60;
    const dateStr = format(date, "yyyy-MM-dd");
    const payload = {
      service_id: serviceId,
      date: dateStr,
      time,
      duration,
      notes: notes || undefined,
      name: name || undefined,
      phone: phone || undefined,
      session_id: sessionId || undefined,
    };

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/appointments-public`, payload);
      const apiData = res.data?.data ?? res.data;
      const appointment = {
        id: apiData?.id,
        service_id: apiData?.service?.id ?? serviceId,
        service_name: apiData?.service?.name ?? serviceName,
        date: apiData?.requested_date ?? dateStr,
        time: (apiData?.requested_time ?? time).replace(/:\d{2}$/, "") || time,
        duration: apiData?.duration ?? duration,
        name: apiData?.client?.name ?? name,
        phone: apiData?.client?.phone ?? phone,
        notes: apiData?.client_notes ?? notes,
        status: apiData?.status,
        ...apiData,
      };
      toast.success("Appointment submitted successfully");
      navigate("/appointment-detail", { state: { appointment }, replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to submit appointment";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  if (sessionValid === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4 text-slate-600">
          <Loader2 className="h-10 w-10 animate-spin" />
          <p>Validating session...</p>
        </div>
      </div>
    );
  }

  if (sessionValid === false) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-lg">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-amber-600" />
              </div>
            </div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Page Expired</h2>
            <p className="text-slate-600 text-sm mb-6">
              This link has expired or is invalid. Please return to the chat and click "Schedule Appointment" again after uploading your documents.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl">Booking Appointment</CardTitle>
          <p className="text-sm text-slate-600">
            Select service, date, and time.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Service */}
            <div className="space-y-2">
              <Label htmlFor="service">Service *</Label>
              <Select
                value={serviceId}
                onValueChange={handleServiceChange}
                disabled={loadingServices}
              >
                <SelectTrigger id="service">
                  <SelectValue placeholder={loadingServices ? "Loading..." : "Select service"} />
                </SelectTrigger>
                <SelectContent>
                  {serviceId && (
                    <SelectItem value="__clear__">— Clear selection —</SelectItem>
                  )}
                  {services.length === 0 && !loadingServices && (
                    <SelectItem value="__none__" disabled>
                      No services available
                    </SelectItem>
                  )}
                  {services.map((s) => {
                    const val = getServiceValue(s);
                    const label = getServiceLabel(s);
                    return (
                      <SelectItem key={val} value={val}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label>Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    disabled={!dateEnabled}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "dd MMMM yyyy") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={handleDateChange}
                    disabled={(d) => d < minDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time */}
            <div className="space-y-2">
              <Label>Time *</Label>
              {loadingSlots ? (
                <div className="flex items-center justify-center py-8 text-slate-500">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading slots...
                </div>
              ) : !dateEnabled || !date ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 py-8 text-center text-sm text-slate-500">
                  Select date first
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 py-8 text-center text-sm text-slate-500">
                  <p>No slots available for this date.</p>
                  <p className="text-xs mt-1">Please select another date.</p>
                </div>
              ) : (
                <div className="grid max-h-48 grid-cols-2 gap-3 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/50 p-2">
                  {availableSlots.map((slot) => {
                    const isSelected = time === slot.time;
                    const isPast = isSlotInPast(date, slot.time);
                    return (
                      <button
                        key={slot.time}
                        type="button"
                        onClick={() => !isPast && handleTimeChange(isSelected ? "" : slot.time)}
                        disabled={isPast}
                        className={`rounded-lg border-2 px-4 py-3 text-center text-sm font-medium transition-colors ${
                          isPast
                            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                            : isSelected
                              ? "border-indigo-600 text-indigo-600"
                              : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        {slot.label ?? slot.time}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Optional: Name & Phone */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  disabled={!detailsEnabled}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone number"
                  disabled={!detailsEnabled}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional information..."
                rows={2}
                disabled={!detailsEnabled}
              />
            </div>

            <Button type="submit" className="w-full" disabled={loadingServices || !detailsEnabled || sessionValid !== true || submitting}>
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Clock className="mr-2 h-4 w-4" />
              )}
              {submitting ? "Submitting..." : "Submit Appointment"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
